import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { setHeroVisible } from '@/lib/shutter/hero-layer-store';

export const NAV_OFFSET = 64;

/** Immediate, offset-aware scroll to a Home section. Returns false if the section is not mounted. */
export function scrollToHomeSection(id: string): boolean {
  const el = document.getElementById(id);
  if (!el) return false;
  const top = el.getBoundingClientRect().top + window.scrollY - NAV_OFFSET;
  window.scrollTo({ top: Math.max(0, top), behavior: 'instant' });
  return true;
}

/** Hide the Hero layer (no shutter), wait for layout, then jump to the section. */
function jumpToSection(id: string) {
  setHeroVisible(false);
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      if (scrollToHomeSection(id)) window.history.replaceState(window.history.state, '', `#${id}`);
    });
  });
}

/**
 * One shared helper for every "Home section" link (nav, mobile menu, footer).
 * Direct jump only — the shutter is reserved for scroll gestures between Hero and Work.
 */
export function useGoToHomeSection() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  return useCallback((id: string) => {
    if (pathname === '/') { jumpToSection(id); return; }
    navigate(`/#${id}`);
  }, [pathname, navigate]);
}

/** Logo click while already on Home: return to the Hero directly, no shutter. */
export function goToHomeTop() {
  window.scrollTo({ top: 0, behavior: 'instant' });
  setHeroVisible(true);
  window.history.replaceState(window.history.state, '', window.location.pathname);
}